import type { PositionRow } from "@/api/types";
import { EntityChip } from "@/components/ui";
import { Panel } from "@/components/ui";
import { formatGwei, formatPct } from "@/lib/formatters";

interface PositionLeaderboardProps {
  data: PositionRow[];
  loading?: boolean;
}

const COLS = [
  {
    key: "entity",
    label: "ENTITY",
    flex: 2,
    minWidth: 140,
    tip: "Address or labelled entity that submitted transactions",
  },
  {
    key: "avg_fee_gwei",
    label: "AVG FEE",
    flex: 1,
    minWidth: 80,
    tip: "Average priority fee per gas paid, in Gwei",
  },
  {
    key: "avg_tip_rank",
    label: "AVG RANK",
    flex: 1,
    minWidth: 80,
    tip: "Average position of this entity's txs within the block, ordered by fee (1 = first)",
  },
  {
    key: "top3_position_share",
    label: "TOP-3 SHARE",
    flex: 1,
    minWidth: 80,
    tip: "Share of transactions landing in the top 3 positions of the block",
  },
  {
    key: "fee_efficiency",
    label: "FEE EFF.",
    flex: 1,
    minWidth: 80,
    tip: "Ratio of block position gained per unit of fee paid. Higher = better value for money",
  },
  {
    key: "delta_rank_vs_position",
    label: "DELTA",
    flex: 1,
    minWidth: 70,
    tip: "Positions jumped vs. expected rank. Positive (red) = paid extra to move up. Negative (green) = got a better slot than fee alone would predict",
  },
];

const cellBase = {
  padding: "0 8px",
  fontSize: 12,
  flex: 1,
  minWidth: 80,
  overflow: "hidden",
} as const;

export function PositionLeaderboard({ data, loading }: PositionLeaderboardProps) {
  return (
    <Panel
      title="ENTITY POSITION LEADERBOARD"
      description="Top 50 addresses ranked by how aggressively they use priority fees to improve their block position. Hover column headers for definitions."
    >
      <div style={{ overflowX: "auto" }}>
        {/* header */}
        <div
          style={{
            display: "flex",
            width: "100%",
            borderBottom: "1px solid var(--color-border)",
            background: "var(--color-bg-panel)",
          }}
        >
          {COLS.map((col) => (
            <div
              key={col.key}
              title={col.tip}
              style={{
                flex: col.flex,
                minWidth: col.minWidth,
                padding: "6px 8px",
                fontSize: 10,
                fontWeight: 500,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                color: "var(--color-text-muted)",
                cursor: "help",
              }}
            >
              {col.label}
            </div>
          ))}
        </div>

        {loading ? (
          <div style={{ padding: 16 }}>
            {Array.from({ length: 5 }, (_, i) => (
              <div
                key={i}
                className="skeleton"
                style={{ height: 28, marginBottom: 4, borderRadius: 3 }}
              />
            ))}
          </div>
        ) : data.length === 0 ? (
          <div
            style={{
              padding: "32px 0",
              textAlign: "center",
              color: "var(--color-text-muted)",
              fontSize: 12,
            }}
          >
            No data for this range
          </div>
        ) : (
          data.map((row, idx) => {
            const deltaColor =
              row.delta_rank_vs_position > 0
                ? "var(--color-red)"
                : row.delta_rank_vs_position < 0
                  ? "var(--color-green)"
                  : "var(--color-text-muted)";
            return (
              <div
                key={idx}
                style={{
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                  borderBottom: "1px solid var(--color-border)",
                  height: 36,
                }}
              >
                <div style={{ flex: 2, minWidth: 140, padding: "0 8px", overflow: "hidden" }}>
                  <EntityChip name={row.entity_name} />
                </div>
                <div className="tabular" style={cellBase}>
                  {formatGwei(row.avg_fee_gwei)}
                </div>
                <div className="tabular" style={cellBase}>
                  {row.avg_tip_rank.toFixed(1)}
                </div>
                <div className="tabular" style={cellBase}>
                  {formatPct(row.top3_position_share)}
                </div>
                <div className="tabular" style={cellBase}>
                  {row.fee_efficiency.toFixed(3)}
                </div>
                <div
                  className="tabular"
                  style={{ ...cellBase, color: deltaColor, fontWeight: 600 }}
                >
                  {row.delta_rank_vs_position > 0 ? "+" : ""}
                  {row.delta_rank_vs_position.toFixed(1)}
                </div>
              </div>
            );
          })
        )}
      </div>
    </Panel>
  );
}
